import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import { ReactComponent as SupplierIcon } from 'assets/icons/warehouse.svg';
import { clsx } from 'clsx';
import Checkbox from 'components/Checkbox/Checkbox';

import { IS_VC_UNIT_CARD_DETAILS_VISIBLE } from '../../../../../../constants/general';
import { formatRevenueAndPercentage } from '../../../../../../utils/helpers';
import CustomNodeItem from '../../BusinessStructure/components/CustomNodeItem';

import type { ISupplierNode } from '../interfaces/ISupplierNode';

import styles from '../sass/CustomUnitNode.module.scss';

function SupplierNode({
  name,
  profitCenterIds,
  naceSection,
  addressCountry,
  keySupplierTier1OfTotal,
  totalCost,
  isCollapsed,
  isDraft,
  showDraftDeleteButton = true,
  cardsInsideCount,
  onDraftDelete,
  aiPanelSelectionInputName,
  isAIPanelSelectionEnabled,
  isAIPanelSelected,
  isAISessionPreviewSelected,
  onToggleAIPanelSelection,
}: ISupplierNode) {
  const { t } = useTranslation();
  const shouldShowAIPanelCheckbox = Boolean(isAIPanelSelectionEnabled || isAISessionPreviewSelected);
  const isAIPanelCheckboxReadonly = Boolean(isAISessionPreviewSelected && !isAIPanelSelectionEnabled);

  const handleAIPanelSelectionChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    event.stopPropagation();

    if (isAIPanelCheckboxReadonly) {
      return;
    }

    onToggleAIPanelSelection?.(event.target.checked);
  };

  const toggleAIPanelSelection = () => {
    if (!isAIPanelSelectionEnabled || isAIPanelCheckboxReadonly) {
      return;
    }

    onToggleAIPanelSelection?.(!isAIPanelSelected);
  };

  const handleHeaderClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('label') || target.tagName === 'INPUT') {
      return;
    }

    event.stopPropagation();
    toggleAIPanelSelection();
  };

  const customNodeItems = [
    {
      title: t('valueChainMap.profitCenterId'),
      value: profitCenterIds?.join(', ') || '-',
    },
    {
      title: t('valueChainMap.naceSection'),
      value: naceSection || '-',
      isCapitalize: true,
    },
    {
      title: t('valueChainMap.country'),
      value: addressCountry || '-',
      isCapitalize: true,
    },
    {
      title: t('valueChainMap.keySupplierTier1Total'),
      value: formatRevenueAndPercentage({
        actual: totalCost?.amount,
        currency: totalCost?.currency,
        percentage: keySupplierTier1OfTotal?.toString(),
      }),
    },
  ];

  const headerClassName = clsx(styles.header, {
    [styles.aiPanelSelectableHeader]: isAIPanelSelectionEnabled,
  });

  const headerContent = (
    <>
      <div className={styles.headerLeft}>
        {shouldShowAIPanelCheckbox ? (
          <Checkbox
            name={aiPanelSelectionInputName || `ai_supplier_select_${name}`}
            value={name}
            checked={Boolean(isAIPanelSelected || isAISessionPreviewSelected)}
            disabled={isAIPanelCheckboxReadonly}
            onChange={handleAIPanelSelectionChange}
            className={clsx(styles.aiPanelCheckbox, {
              [styles.aiPanelCheckboxReadonly]: isAIPanelCheckboxReadonly,
            })}
            size="medium"
          />
        ) : null}
        <SupplierIcon />
        <p className={styles.title}>{name}</p>
      </div>
      <div className={styles.headerRight}>
        {isCollapsed && cardsInsideCount && cardsInsideCount > 0 ? (
          <span className={styles.cardsCount}>{cardsInsideCount}</span>
        ) : null}
        {isDraft && showDraftDeleteButton ? (
          <button
            type="button"
            className={styles.draftDeleteButton}
            aria-label={t('common.delete')}
            onClick={(event) => {
              event.stopPropagation();
              onDraftDelete?.();
            }}
          >
            <TrashIcon className={styles.draftDeleteIcon} />
          </button>
        ) : null}
      </div>
    </>
  );

  return (
    <>
      {isAIPanelSelectionEnabled ? (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={headerClassName}
          onClick={handleHeaderClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              toggleAIPanelSelection();
            }
          }}
          role="button"
          tabIndex={0}
        >
          {headerContent}
        </div>
      ) : (
        <div className={headerClassName}>
          {headerContent}
        </div>
      )}
      {IS_VC_UNIT_CARD_DETAILS_VISIBLE && !isCollapsed ? (
        <div className={styles.content}>
          {customNodeItems.map(({ title, value, isCapitalize }) => (
            <CustomNodeItem
              key={`customNodeItem_${title}`}
              title={title}
              value={value}
              isCapitalize={isCapitalize}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

export default SupplierNode;
